import type { Recipe } from '../schemas/data/recipes.js';

export const RECIPES: Recipe[] = [
  {
    id: 'heart-charm',
    name: 'Heart Charm',
    description: 'A little charm to help you befriend a wild squishy.',
    inputs: { timber: 2, treats: 1 }, // TUNE:
    output: { resource: 'heart-charm', quantity: 1 },
    craftSeconds: 60, // TUNE:
  },
  // #238: Treats cooked from Greens (owner decision, the nesting economy);
  // farm plots still grow them, and that stays the best way.
  {
    id: 'cook-treats',
    name: 'Cooked Treats',
    description: 'Stir up some Greens into yummy Treats for your squishies.',
    inputs: { greens: 2 }, // TUNE:
    output: { resource: 'treats', quantity: 3 }, // TUNE:
    craftSeconds: 60, // TUNE:
  },
  // #238: Ice without mountains. Slow on its own; a Frost squishy on the
  // team freezes it twice as fast.
  {
    id: 'freeze-water',
    name: 'Frozen Water',
    description:
      'Leave Water out in the cold until it turns into sparkly Ice. A Frost squishy on your team makes it twice as fast!',
    inputs: { water: 3 }, // TUNE:
    output: { resource: 'ice', quantity: 1 },
    craftSeconds: 30 * 60, // TUNE:
    fasterWith: { element: 'frost', percent: 50 }, // TUNE:
  },
  // #735: Ice's second use (besides the Ice Wall). Year-round, like Ice
  // itself: a feed Frost squishies love (`CARE_RULES.foods`).
  {
    id: 'mix-frosty-treats',
    name: 'Frosty Treats',
    // The item's own line says Frost squishies love them; the page shows both.
    description: 'Crunch Ice and Treats into a chilly, sparkly snack.',
    inputs: { ice: 2, treats: 1 }, // TUNE:
    output: { resource: 'frosty-treats', quantity: 3 }, // TUNE:
    craftSeconds: 2 * 60, // TUNE: like Pies
  },
  // Wild Isle (#621 W1-8b): Flour, Fish and Salt come only from its works,
  // so Pies are a Wild Isle feed (`CARE_RULES.foods`).
  {
    id: 'bake-pies',
    name: 'Pies',
    description:
      'Bake Flour, Fish and a pinch of Salt into warm pies. Squishies go wild for these!',
    inputs: { flour: 2, fish: 1, salt: 1 }, // TUNE: owner answer Q5 (#621 plan §8)
    output: { resource: 'pies', quantity: 3 }, // TUNE:
    craftSeconds: 2 * 60, // TUNE:
  },
  // Battle potions (#214): farm Treats plus something gathered. Year-round
  // inputs, so a potion never needs a season.
  {
    id: 'brave-brew',
    name: 'Brave Brew',
    description: 'Stir Treats and a pinch of Stone into a fizzy, peppery brew.',
    inputs: { treats: 2, stone: 2 }, // TUNE:
    output: { resource: 'brave-brew', quantity: 1 },
    craftSeconds: 2 * 60, // TUNE:
  },
  {
    id: 'cozy-cocoa',
    name: 'Cozy Cocoa',
    description: 'Warm Treats over a little Timber fire. Marshmallows on top!',
    inputs: { treats: 2, timber: 2 }, // TUNE:
    output: { resource: 'cozy-cocoa', quantity: 1 },
    craftSeconds: 2 * 60, // TUNE:
  },
  {
    id: 'hearty-soup',
    name: 'Hearty Soup',
    description: 'Simmer Treats on Emberwood until it smells amazing.',
    inputs: { treats: 2, emberwood: 1 }, // TUNE:
    output: { resource: 'hearty-soup', quantity: 1 },
    craftSeconds: 2 * 60, // TUNE:
  },
  {
    id: 'pumpkin-treats',
    name: 'Pumpkin Treats',
    description: 'Turn a pumpkin into a pile of spooky-sweet snacks.',
    inputs: { pumpkins: 1 }, // TUNE:
    output: { resource: 'treats', quantity: 3 }, // TUNE:
    craftSeconds: 30, // TUNE:
    season: 'halloween',
  },
  {
    id: 'jack-o-lantern-hearthfire',
    name: "Jack-o'-Lantern",
    description:
      'Carve a big pumpkin with a glowing grin. Then build it into a fire out on your land. Boo!',
    inputs: { pumpkins: 3, emberwood: 2, 'witch-dust': 1 }, // TUNE:
    output: { resource: 'jack-o-lantern-hearthfire', quantity: 1 }, // built into a fire out on your land (#202)
    craftSeconds: 5 * 60, // TUNE:
    season: 'halloween',
  },
  // Explore tools (#199): each makes a tool's worth of uses (`EXPLORE_RULES.tools`),
  // and every search with it spends one.
  {
    id: 'shovel',
    name: 'Shovel',
    description: 'A sturdy little Shovel for digging up mounds on your land. Lasts 20 digs!',
    inputs: { timber: 2, stone: 2 }, // TUNE:
    output: { resource: 'shovel', quantity: 20 },
    craftSeconds: 60, // TUNE:
  },
  {
    id: 'net',
    name: 'Snorkel',
    description: 'Weave Greens round a Timber tube for a comfy Snorkel. Lasts 20 dives!',
    inputs: { timber: 1, greens: 3 }, // TUNE:
    output: { resource: 'net', quantity: 20 },
    craftSeconds: 60, // TUNE:
  },
  {
    id: 'rope',
    name: 'Walking Stick',
    description:
      'Whittle Timber into a trusty Walking Stick for the mountain trail. Lasts 10 hikes!',
    inputs: { timber: 2, greens: 1 }, // TUNE: #335, was 4 Greens as a Rope
    output: { resource: 'rope', quantity: 10 },
    craftSeconds: 60, // TUNE:
  },
  {
    id: 'lantern',
    name: 'Lantern',
    description: 'A glowing Lantern to peek inside dark caves. Lasts 15 caves!',
    inputs: { timber: 1, glimmer: 1, emberwood: 1 }, // TUNE:
    output: { resource: 'lantern', quantity: 15 },
    craftSeconds: 2 * 60, // TUNE:
  },
  // Clearing tools (#242, owner decision 2026-10-11 Q4): each makes a tool's
  // worth of clears (`CLEARING_RULES.tools`), and every clear spends one.
  {
    id: 'axe',
    name: 'Axe',
    description: 'A trusty Axe to clear a forest or old forest on your homestead. Lasts 3 clears!',
    inputs: { timber: 4, stone: 2 }, // TUNE: #295 plan revision 1
    output: { resource: 'axe', quantity: 3 },
    craftSeconds: 60, // TUNE:
  },
  {
    id: 'pickaxe',
    name: 'Pickaxe',
    description: 'A sturdy Pickaxe to clear a mountain on your homestead. Lasts 3 clears!',
    inputs: { timber: 2, stone: 4 }, // TUNE: #295 plan revision 1
    output: { resource: 'pickaxe', quantity: 3 },
    craftSeconds: 60, // TUNE:
  },
  {
    id: 'spade',
    name: 'Spade',
    description:
      'A handy Spade to clear a meadow, hill, lake or pumpkin field on your homestead. Lasts 3 clears!',
    inputs: { timber: 3, stone: 3 }, // TUNE: #295 plan revision 1
    output: { resource: 'spade', quantity: 3 },
    craftSeconds: 60, // TUNE:
  },
  {
    // Thanksgiving's use for Magic Fallen Leaves (owner decision 2026-10-06):
    // cheaper Heart Charms while the season's squishy is out.
    id: 'leafy-heart-charms',
    name: 'Leafy Heart Charms',
    description:
      'Make two Heart Charms wrapped in crunchy golden leaves. Squishies love the crinkle!',
    inputs: { 'magic-fallen-leaves': 4, treats: 1 }, // TUNE:
    output: { resource: 'heart-charm', quantity: 2 }, // TUNE:
    craftSeconds: 60, // TUNE: same as a plain Heart Charm
    season: 'thanksgiving',
  },
  // Rekindled recipes (#306 PR 5): each page is sealed by an ingredient found
  // only in Hollowed villages (`joy-ember`, `heart-petals`), so it opens the
  // first time a kid finds one there, never by gathering.
  {
    id: 'joy-lantern',
    name: 'Joy Lantern',
    description:
      'A lantern with a happy heart flame. Built on your land, it shines a whole tile further than a new Hearthfire!',
    inputs: { 'joy-ember': 1, emberwood: 3, glimmer: 1 }, // TUNE:
    output: { resource: 'joy-lantern', quantity: 1 }, // built into a fire out on your land
    craftSeconds: 5 * 60, // TUNE: like the Jack-o'-Lantern
  },
  {
    id: 'heart-tea',
    name: 'Heart Tea',
    description: 'Steep Heart Petals in warm Water for two cups of cozy tea. Squishies love it!',
    inputs: { 'heart-petals': 1, water: 2, treats: 1 }, // TUNE:
    output: { resource: 'heart-tea', quantity: 2 }, // TUNE:
    craftSeconds: 2 * 60, // TUNE: like the potions
  },
];
